import { useSelector } from "react-redux";
import { selectUIVersion } from "store/app";

import { Box, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import { useDispatch } from "react-redux";

import ToolTip from "components/ToolTip";
import { displayDetailsModal } from "store/app/appSlice";
import { Button, SvgOrImg } from "components";
import { CheckmarkIcon, InfoIcon, EmptyImageCrossIcon } from "components/icons";
import * as styles from "./ButtonWithImageOptions.styles";

export default function ButtonWithImageOptions({
  customisationTitle,
  options = [],
  isDisabled,
  isSelected: isSelectedOverride,
  value: selectedValue,
  onChange,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);

  const dispatch = useDispatch();

  const items = options.map((option) => {
    const isSelected = selectedValue === option.value;

    const isSelectedOrOverride = isSelectedOverride
      ? isSelectedOverride
      : isSelected;

    return {
      ...option,
      isSelectedOrOverride,
      onClick: () => onChange(option.value),
    };
  });

  if (!isMobile) {
    return items
      .map(
        ({
          iconAfter,
          label,
          value,
          isSelectedOrOverride,
          onClick,
          toolTip,
        }) => {
          const iconAfterUrl = iconAfter?.url && (
            <SvgOrImg
              type={iconAfter?.type}
              src={iconAfter.url}
              alt={iconAfter?.alt}
            />
          );
          const iconAfterCss = iconAfter?.css && (
            <Box
              sx={{
                ...styles.iconCSSContainer({
                  ...(iconAfter?.css || {}),
                }),
              }}
            />
          );
          return {
            key: value,
            toolTip,
            component: (
              <Button
                key={value}
                variant="secondary"
                size="large"
                iconBefore={isSelectedOrOverride && <CheckmarkIcon />}
                iconAfter={
                  iconAfterUrl || iconAfterCss || <EmptyImageCrossIcon />
                }
                isRound
                isDisabled={isDisabled}
                isSelected={isSelectedOrOverride}
                onClick={onClick}
                sx={styles.buttonWithImage({
                  size: "large",
                  isSelectedOrOverride,
                })}
              >
                {label}
              </Button>
            ),
          };
        }
      )
      .map(({ key, toolTip, component }) => {
        if (!toolTip) {
          return component;
        }
        return (
          <ToolTip key={key} toolTip={toolTip}>
            {component}
          </ToolTip>
        );
      });
  }

  return items.map(
    ({
      iconAfter,
      label,
      value,
      isSelectedOrOverride,
      onClick,
      hint,
      toolTip,
    }) => {
      const iconAfterUrl = iconAfter?.url && (
        <SvgOrImg
          type={iconAfter?.type}
          src={iconAfter.url}
          alt={iconAfter?.alt}
        />
      );
      const iconAfterCss = iconAfter?.css && (
        <Box
          sx={{
            ...styles.iconCSSContainer({
              ...(iconAfter?.css || {}),
            }),
          }}
        />
      );

      const MobileButton = (
        <Button
          variant="secondary"
          size="medium"
          iconBefore={isSelectedOrOverride && <CheckmarkIcon />}
          iconAfter={iconAfterUrl || iconAfterCss || <EmptyImageCrossIcon />}
          isRound
          isDisabled={isDisabled}
          isSelected={isSelectedOrOverride}
          onClick={onClick}
          sx={styles.buttonWithImage({ size: "medium", UIVersion })}
        >
          {label}
        </Button>
      );

      return (
        <Box key={value} sx={styles.item({ UIVersion })}>
          {toolTip && UIVersion === 2 ? (
            <ToolTip toolTip={toolTip}>{MobileButton}</ToolTip>
          ) : (
            MobileButton
          )}
          {hint && (
            <Box sx={styles.infoIconContainer}>
              <ThemeUiButton
                variant="unstyled"
                sx={styles.infoIconButton}
                onClick={() => {
                  dispatch(
                    displayDetailsModal({
                      title: customisationTitle,
                      subTitle: label,
                      ...hint,
                    })
                  );
                }}
              >
                <InfoIcon />
              </ThemeUiButton>
            </Box>
          )}
        </Box>
      );
    }
  );
}
